import { tv } from 'tailwind-variants'

// Top + right border lines drawn via ::after background gradients instead of CSS borders.
// CSS borders suffer from sub-pixel rounding at fractional zoom levels on Windows (Chrome),
// causing every-other-row borders to vanish. Background fills are immune to this.
// Lines are drawn at the TOP of each cell so they can't be covered by the next row's cells.
// --border-w is set on the Rows container to 1/devicePixelRatio px, ensuring exactly 1 device
// pixel regardless of zoom. Falls back to 1px when the variable is not set.
//
// IMPORTANT: These must be plain strings (no template literals) so Tailwind's JIT scanner can
// detect and generate the CSS for each class.
//
// Every grid line is the divider colour, down the columns as well as along the rows, so the body
// reads as the same grid as the header and footer (the "LIGHT" names are historical: the column
// lines were once surface-sunken, nearly invisible on white).

const BORDER_BOTTOM_GREY_RIGHT_LIGHT =
  "after:content-[''] after:absolute after:inset-0 after:pointer-events-none after:z-[1] after:bg-[linear-gradient(to_bottom,var(--color-divider)_var(--border-w,1px),transparent_var(--border-w,1px)),linear-gradient(to_left,var(--color-divider)_var(--border-w,1px),transparent_var(--border-w,1px))]"
const BORDER_RIGHT_LIGHT_ONLY =
  "after:content-[''] after:absolute after:inset-0 after:pointer-events-none after:z-[1] after:bg-[linear-gradient(to_left,var(--color-divider)_var(--border-w,1px),transparent_var(--border-w,1px))]"
const BORDER_BOTTOM_GREY_RIGHT_GREY =
  'after:bg-[linear-gradient(to_bottom,var(--color-divider)_var(--border-w,1px),transparent_var(--border-w,1px)),linear-gradient(to_left,var(--color-divider)_var(--border-w,1px),transparent_var(--border-w,1px))]'
const BORDER_BOTTOM_GREY_ONLY =
  'after:bg-[linear-gradient(to_bottom,var(--color-divider)_var(--border-w,1px),transparent_var(--border-w,1px))]'
const BORDER_RIGHT_GREY_ONLY =
  'after:bg-[linear-gradient(to_left,var(--color-divider)_var(--border-w,1px),transparent_var(--border-w,1px))]'

// When a cell is selected (header or column)
export const SELECTED_TINT = 'before:absolute before:inset-0 before:bg-blue/15 before:pointer-events-none before:z-[1]'

export const cellStyles = tv({
  slots: {
    container: `absolute cursor-default contain-[layout_style] ${BORDER_BOTTOM_GREY_RIGHT_LIGHT}`,
    comments: 'box-border table',
    value:
      'overflow-hidden text-ellipsis break-words w-screen bg-inherit pr-[8px] [padding-left:var(--cell-pl,8px)] table-cell align-middle focus:outline-none',
    editing:
      'block w-full bg-surface outline-none m-0 [padding-left:var(--cell-pl,8px)] pr-[8px] h-inherit focus:outline-none',
    content: 'w-full'
  },
  variants: {
    focused: {
      true: {
        editing: 'border border-blue'
      }
    },
    searched: {
      true: { container: 'bg-really-light-yellow!' }
    },
    readOnly: {
      true: { container: 'bg-surface-sunken' }
    },
    selectableText: {
      true: { container: 'select-text' }
    },
    expanded: {
      true: { container: `bg-surface border-t border-t-divider relative ${BORDER_RIGHT_LIGHT_ONLY}` }
    },
    inRange: {
      true: { container: SELECTED_TINT }
    },
    frozen: {
      true: { container: 'sticky z-[3]' }
    },
    editingText: {
      true: { container: 'z-[2] overflow-visible' }
    },
    canDrag: {
      true: { container: 'cursor-grab' }
    },
    expandedText: {
      true: { container: 'h-auto max-h-none min-h-[33px]' }
    },
    lastFrozen: {
      true: { container: 'shadow-[3px_0_1px_var(--color-divider)]' },
      false: { container: 'shadow-none' }
    },
    editing: {
      true: { container: 'z-[2] after:bg-none' }
    },
    rowNumberCell: {
      true: { container: `bg-surface ${BORDER_BOTTOM_GREY_RIGHT_GREY}` }
    },
    fontBold: {
      true: { container: 'font-bold' }
    },
    fontItalic: {
      true: { container: 'italic' }
    },
    fontUnderline: {
      true: { container: 'underline' }
    },
    validation: {
      true: { content: 'w-[calc(100%-25px)]' }
    },
    firstRow: {
      true: { container: BORDER_RIGHT_LIGHT_ONLY }
    },
    lastRow: {
      true: { container: '[border-bottom:var(--border-w,1px)_solid_var(--color-divider)]' }
    },
    inFillRange: {
      true: { container: SELECTED_TINT }
    },
    inSelectedColumn: {
      true: { container: SELECTED_TINT }
    },
    inSelectedRow: {
      true: { container: SELECTED_TINT }
    }
  },
  compoundVariants: [
    {
      frozen: true,
      editing: true,
      class: { container: 'z-[9]' }
    },
    {
      frozen: true,
      editingText: true,
      class: { container: 'z-[9]' }
    },
    {
      frozen: true,
      expanded: true,
      class: { container: 'sticky' }
    },
    {
      rowNumberCell: true,
      frozen: true,
      class: { container: 'z-[10]' }
    },
    {
      rowNumberCell: true,
      expanded: true,
      class: { container: BORDER_BOTTOM_GREY_ONLY }
    },
    {
      rowNumberCell: true,
      editing: true,
      class: { container: BORDER_BOTTOM_GREY_ONLY }
    },
    {
      rowNumberCell: true,
      firstRow: true,
      class: { container: BORDER_RIGHT_GREY_ONLY }
    }
  ]
})

export const aggregateStyles = tv({
  slots: {
    // border-r matches the column header divider so each aggregate lines up with its column,
    // which is otherwise hard to tell apart when the grid has only a few rows. align-top: the
    // first (frozen) cell is inline-block, and on the baseline the line box keeps room for
    // descenders below it, pushing the footer's content 5px past its bottom edge. That made the
    // grid 5px taller than its rows, so at full scroll the sticky footer jumped up by 5px.
    container: 'relative inline-block align-top box-border select-none border-r border-r-divider'
  },
  variants: {
    frozen: {
      true: { container: 'sticky z-[6]' },
      false: { container: 'absolute' }
    },
    lastFrozen: {
      true: { container: 'shadow-[3px_0_1px_var(--color-divider)]' },
      false: { container: 'shadow-none' }
    },
    clickable: {
      true: { container: 'cursor-pointer hover:bg-surface-strong transition-all' }
    }
  }
})

export const groupCellStyles = tv({
  slots: {
    container: 'border-r border-divider'
  },
  variants: {
    frozen: {
      true: { container: 'sticky z-[3]' },
      false: { container: 'absolute z-[1]' }
    },
    // Group cells draw the line along their top edge, as every row does, so the line between
    // groups is the same single line all the way across.
    topLine: {
      true: { container: 'border-t border-t-divider' }
    },
    showValue: {
      true: {}
    },
    withinCollapsedGroup: {
      true: {}
    },
    lastFrozen: {
      true: { container: 'shadow-[3px_0_1px_var(--color-divider)]' },
      false: { container: 'shadow-none' }
    },
    inSelectedColumn: {
      true: { container: SELECTED_TINT }
    },
    fontBold: {
      true: { container: 'font-bold' }
    },
    fontItalic: {
      true: { container: 'italic' }
    },
    fontUnderline: {
      true: { container: 'underline' }
    }
  },
  compoundVariants: [
    {
      frozen: true,
      showValue: true,
      class: { container: 'z-[4]' }
    },
    {
      frozen: false,
      showValue: true,
      class: { container: 'z-[2]' }
    }
  ]
})
